"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { X } from "lucide-react";
import { getContestState, CONTEST_NAME, getContestEndDate } from "@/lib/config";
import { usePathname } from "next/navigation";

function useInlineCountdown(active: boolean) {
  const [out, setOut] = useState<string>("");
  useEffect(() => {
    if (!active) return;
    function tick() {
      const end = getContestEndDate().getTime();
      const diff = Math.max(0, end - Date.now());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff / 3600000) % 24);
      const m = Math.floor((diff / 60000) % 60);
      const s = Math.floor((diff / 1000) % 60);
      const pad = (n: number) => n.toString().padStart(2, "0");
      if (diff <= 0) {
        setOut("Ended");
        return;
      }
      setOut(`${d}d:${pad(h)}h:${pad(m)}m:${pad(s)}s`);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);
  return out;
}

export default function ContestBanner() {
  const [closed, setClosed] = useState(true);
  const [closedPath, setClosedPath] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [state, setState] = useState<ReturnType<typeof getContestState> | null>(null);
  const [top, setTop] = useState(0);
  const countdown = useInlineCountdown(state === "active");

  const pathname = usePathname();

  useEffect(() => {
    setHydrated(true);
    const s = getContestState();
    setState(s);
  }, []);

  // Re-open on route change (page-level visibility) unless user already closed on this path
  useEffect(() => {
    if (!hydrated) return;
    if (state !== "active") return;
  if (pathname?.startsWith('/contest')) { setClosed(true); return; }
    if (closedPath === pathname) return; // user closed for this path, respect it
    setClosed(false);
  }, [pathname, hydrated, state, closedPath]);

  // Measure header + subheader heights and update top & exposed vars
  useEffect(() => {
    if (!hydrated) return;

    let raf = 0;
    const computeNow = () => {
      // Sur la page feed, les banners sont en overlay et se positionnent juste en dessous du feed-top-bar
      const isFeedPage = pathname === '/feed';
      
      if (isFeedPage) {
        // Sur la page feed, le feed-top-bar fait environ 56px de hauteur (py-3 + contenu)
        // Les banners se positionnent juste en dessous
        const feedTopBar = document.querySelector('[data-feed-top-bar]') as HTMLElement | null;
        const feedTopBarHeight = feedTopBar?.offsetHeight || 56;
        setTop(feedTopBarHeight);
        return;
      }
      
      const readCssPx = (name: string, fallback: number) => {
        const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        const n = Number.parseFloat(raw);
        return Number.isFinite(n) ? n : fallback;
      };
      const headerH = readCssPx("--header-height", 0);
      setTop(Math.max(0, headerH));
    };

    const scheduleCompute = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeNow);
    };

    scheduleCompute();

    const ro = new ResizeObserver(scheduleCompute);
    const headerEl = document.querySelector('header') as HTMLElement | null;
    headerEl && ro.observe(headerEl);

    window.addEventListener("resize", scheduleCompute);
    window.addEventListener("scroll", scheduleCompute, { passive: true });

    const mo = new MutationObserver(() => {
      scheduleCompute();
    });
    mo.observe(document.body, { subtree: true, childList: true, attributes: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", scheduleCompute);
      window.removeEventListener("scroll", scheduleCompute);
      if (raf) cancelAnimationFrame(raf);
      mo.disconnect();
    };
  }, [hydrated, pathname]);

  // Expose banner height variable when visible (for padding of content)
  // Sur la page feed, les banners sont en overlay et ne doivent pas affecter le layout
  useEffect(() => {
    if (!hydrated) return;
    const el = document.getElementById("kraahl-contest-banner");
    const update = () => {
      const elNow = document.getElementById("kraahl-contest-banner");
      // Sur la page feed, on ne définit pas la variable car les banners sont en overlay
      if (pathname === '/feed') {
        document.documentElement.style.removeProperty("--contest-banner-height");
        return;
      }
      if (elNow && !closed && state === "active") {
        document.documentElement.style.setProperty(
          "--contest-banner-height",
          elNow.offsetHeight + "px"
        );
      } else {
        document.documentElement.style.removeProperty("--contest-banner-height");
      }
    };

    // Run once immediately to set or remove the variable
    update();

    // If banner visible, observe size changes and window resizes (mobile address bar changes trigger resize)
    let ro: ResizeObserver | null = null;
    if (el && !closed && state === "active") {
      ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);
      window.addEventListener("orientationchange", update);
      // Listen for CSS transitions that may change height
      el.addEventListener("transitionend", update);
    }

    return () => {
      ro && ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      el && el.removeEventListener("transitionend", update);
      // Ensure variable is removed when component unmounts/hidden
      if (!document.getElementById("kraahl-contest-banner")) {
        document.documentElement.style.removeProperty("--contest-banner-height");
      }
    };
  }, [hydrated, closed, state, pathname]);

  if (!hydrated || state !== "active" || closed) return null;

  return (
    <div
      id="kraahl-contest-banner"
  className="fixed left-0 right-0 z-41 bg-foreground text-background dark:bg-foreground dark:text-background border-b border-border/30 shadow-xs backdrop-blur-xs transition-all duration-300 ease-out"
      style={{ top, willChange: 'top' }}
      role="status"
      aria-label="Contest announcement banner"
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2 text-[11px] sm:text-[13px] font-medium leading-snug">
        <span className="inline-flex items-center rounded-full bg-background/25 text-background px-2 py-0.5 text-[9px] sm:text-[10px] tracking-wide uppercase font-semibold shadow-xs">Live</span>
        <p className="flex-1 text-[11px] sm:text-[13px] font-medium">
          🎄 <span className="font-semibold">Kraahl Christmas Contest</span> is live! Win a LEGO set up to $100. <Link href="/contest" className="underline underline-offset-2 font-semibold hover:opacity-90">Learn more</Link>.
        </p>
        {countdown && countdown !== "Ended" && (
          <span className="hidden md:inline-flex items-center gap-1 rounded-full bg-background/20 px-3 py-1 text-[11px] font-semibold tracking-wide font-mono">
            <span className="w-2 h-2 rounded-full bg-background animate-pulse" /> {countdown}
          </span>
        )}
        <button
          aria-label="Close contest banner"
          onClick={() => {
            setClosedPath(pathname);
            setClosed(true);
          }}
          className="shrink-0 rounded-full p-1.5 hover:bg-background/25 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-background/60 transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
